const MIN_TEXT_LENGTH = 100;
const MAX_TEXT_LENGTH = 1000;

// Simple inline elements that we should try to expand beyond
const SIMPLE_TAGS = new Set([
  'SPAN',
  'STRONG',
  'EM',
  'B',
  'I',
  'A',
  'MARK',
  'CODE',
  'SMALL',
  'SUB',
  'SUP',
]);

/**
 * Checks if an element is "too simple" and should be expanded.
 * An element is too simple if it's a simple inline tag or has very little content.
 */
const isTooSimple = (element: HTMLElement, minLength: number): boolean => {
  const textLength = element.textContent.length;

  // Too short
  if (textLength < minLength) {
    return true;
  }

  // Is a simple inline element
  if (SIMPLE_TAGS.has(element.tagName)) {
    return true;
  }

  // Is a single-child element where the child has the same text (just a wrapper)
  const children = element.children;
  const firstChild = children[0];
  if (
    children.length === 1 &&
    firstChild?.textContent.trim() === element.textContent.trim()
  ) {
    return true;
  }

  return false;
};

/**
 * Extracts an HTML snippet around matched text from parsed HTML content.
 * Finds a "Goldilocks" size - not too small (expands simple elements) and
 * not too large (caps at max length).
 *
 * @param htmlContent - The full HTML string to search in
 * @param matchedText - The text to find within the HTML
 * @param maxParentLevels - Maximum parent levels to traverse up (default: 5)
 * @returns The outerHTML of the contextual parent element, or null if not found
 */
export const extractHtmlSnippet = (
  htmlContent: string,
  matchedText: string,
  maxParentLevels = 5,
): string | null => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  // Use TreeWalker to find text nodes containing the matched text
  const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
    acceptNode: node => {
      const hasMatch = node.textContent
        ?.toLowerCase()
        .includes(matchedText.toLowerCase());
      return hasMatch ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  const textNode = walker.nextNode();
  if (!textNode) return null;

  // Track the best candidate as we traverse up
  let bestCandidate: HTMLElement | null = null;

  // Traverse up parent levels to find the "Goldilocks" element
  let element: Node | null = textNode;
  for (
    let level = 0;
    level < maxParentLevels && element.parentElement;
    level++
  ) {
    element = element.parentElement;
    const htmlElement = element as HTMLElement;
    const textLength = htmlElement.textContent.length;

    // Skip if we've hit the body
    if (htmlElement.tagName === 'BODY') {
      break;
    }

    // If this element is too large, stop here
    if (textLength > MAX_TEXT_LENGTH) {
      break;
    }

    // If this element is not too simple, it's a good candidate
    if (!isTooSimple(htmlElement, MIN_TEXT_LENGTH)) {
      bestCandidate = htmlElement;
      // Keep going to see if we can get a slightly larger, better context
      // But stop if we're already at a good size
      if (textLength >= MIN_TEXT_LENGTH * 2) {
        break;
      }
    } else {
      // Even if too simple, track it as a fallback
      bestCandidate = htmlElement;
    }
  }

  if (!bestCandidate) {
    return null;
  }

  const textLength = bestCandidate.textContent.length;

  // If still too large somehow, truncate
  if (textLength > MAX_TEXT_LENGTH) {
    return truncateAroundMatch(bestCandidate.textContent, matchedText);
  }

  return bestCandidate.outerHTML;
};

/**
 * Truncates text content around the matched text, preserving context on both sides.
 */
const truncateAroundMatch = (text: string, matchedText: string): string => {
  const lowerText = text.toLowerCase();
  const lowerMatch = matchedText.toLowerCase();
  const matchIndex = lowerText.indexOf(lowerMatch);

  if (matchIndex === -1) {
    // Match not found, just return truncated text from the start
    return text.slice(0, MAX_TEXT_LENGTH) + '...';
  }

  // Calculate how much context to show on each side
  const contextLength = Math.floor((MAX_TEXT_LENGTH - matchedText.length) / 2);
  const start = Math.max(0, matchIndex - contextLength);
  const end = Math.min(
    text.length,
    matchIndex + matchedText.length + contextLength,
  );

  let result = text.slice(start, end);

  // Add ellipsis if truncated
  if (start > 0) {
    result = '...' + result;
  }
  if (end < text.length) {
    result = result + '...';
  }

  return result;
};
